/* ==========================================================================
   MAISON MILAN — Product gallery arrows
   Dawn's thumbnail_slider gives the thumbnail strip its own arrows but
   leaves the main image with none, so stepping through the shots means
   aiming at a 60px target. These drive the existing thumbnails rather than
   duplicating the gallery's state: whatever Dawn does on a thumbnail click
   is exactly what happens here.
   ========================================================================== */
class MaisonGallery extends HTMLElement {
  connectedCallback() {
    this.gallery = this.closest('media-gallery') || document.querySelector('media-gallery');
    if (!this.gallery) return;

    this.thumbs = Array.from(
      this.gallery.querySelectorAll('.thumbnail-list__item button.thumbnail, .thumbnail-list__item .thumbnail')
    );
    // One image needs no navigation.
    if (this.thumbs.length < 2) return;

    this.prev = this.build('prev', 'Previous image', 'M15 5l-7 7 7 7');
    this.next = this.build('next', 'Next image', 'M9 5l7 7-7 7');
    this.append(this.prev, this.next);

    this.prev.addEventListener('click', () => this.step(-1));
    this.next.addEventListener('click', () => this.step(1));

    // Dawn's counter reports which page the thumbnail strip is scrolled to,
    // not which image is showing, so it sits on "1" however you move through
    // the gallery. Watching aria-current catches every route — these arrows,
    // a thumbnail click, and the re-render on a variant change.
    this.counter = this.gallery.querySelector('.slider-counter--current');
    if (this.counter) {
      this.observer = new MutationObserver(() => this.syncCounter());
      this.thumbs.forEach((t) =>
        this.observer.observe(t, { attributes: true, attributeFilter: ['aria-current'] })
      );
      this.syncCounter();
    }

    // Dawn re-renders the gallery on a variant change, so the active state is
    // read at click time rather than cached here.
    this.hidden = false;
  }

  build(kind, label, path) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = `mm-gallery__arrow mm-gallery__arrow--${kind}`;
    button.setAttribute('aria-label', label);
    button.innerHTML =
      `<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="${path}"/></svg>`;
    return button;
  }

  currentIndex() {
    const i = this.thumbs.findIndex(
      (t) => t.getAttribute('aria-current') === 'true' || t.closest('[aria-current="true"]')
    );
    return i === -1 ? 0 : i;
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  syncCounter() {
    if (!this.counter) return;
    this.counter.textContent = this.currentIndex() + 1;
  }

  step(direction) {
    const count = this.thumbs.length;
    const next = (this.currentIndex() + direction + count) % count;
    this.thumbs[next].click();
    // Keep the chosen thumbnail in view in its own scroller.
    this.thumbs[next].scrollIntoView({ block: 'nearest', inline: 'center' });
  }
}

if (!customElements.get('maison-gallery')) {
  customElements.define('maison-gallery', MaisonGallery);
}

/* The element is appended next to the main media so it can be positioned
   against it without touching Dawn's markup. */
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('media-gallery').forEach((gallery) => {
    const stage = gallery.querySelector('.product__media-list, .product__media-wrapper');
    if (!stage || stage.querySelector('maison-gallery')) return;
    const holder = document.createElement('maison-gallery');
    holder.className = 'mm-gallery';
    stage.parentElement.insertBefore(holder, stage.nextSibling);
  });
});
